import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * Lightweight WebView resilience for Capacitor apps loading production URL.
 * - Refreshes content on app resume (avoids stale cache)
 * - Shows offline indicator when network is unavailable
 * - No aggressive reload loops
 */
export const useWebViewResilience = () => {
  const lastActiveRef = useRef<number>(Date.now());
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!isNative) return;

    // Refresh on resume if stale (>5 minutes in background)
    const STALE_THRESHOLD_MS = 5 * 60 * 1000;

    const handleAppStateChange = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        const elapsed = Date.now() - lastActiveRef.current;
        if (elapsed > STALE_THRESHOLD_MS) {
          // Soft refresh: reload current page to get fresh content
          window.location.reload();
        }
      } else {
        lastActiveRef.current = Date.now();
      }
    });

    return () => {
      handleAppStateChange.then(listener => listener.remove());
    };
  }, [isNative]);

  useEffect(() => {
    // Listen for online/offline events to show user feedback
    const handleOffline = () => {
      // Create minimal offline indicator if not already present
      if (!document.getElementById('offline-indicator')) {
        const indicator = document.createElement('div');
        indicator.id = 'offline-indicator';
        indicator.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          padding: 12px;
          background: hsl(0 84% 60%);
          color: white;
          text-align: center;
          z-index: 99999;
          font-family: system-ui, sans-serif;
          font-size: 14px;
        `;
        indicator.textContent = 'You are offline. Some features may not work.';
        document.body.prepend(indicator);
      }
    };

    const handleOnline = () => {
      const indicator = document.getElementById('offline-indicator');
      if (indicator) {
        indicator.remove();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Check initial state
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
};
